import React, { useCallback, useMemo, useState } from "react";

import AutocompleteSelectInput from "veneer/AutocompleteSelectInput";
import Drawer from "veneer/Drawer";
import Stack from "veneer/Stack";
import TextInput from "veneer/TextInput";

import { useAppDispatch } from "cortex/ui/src/hooks";
import { VistarNetwork } from "cortex/ui/src/network/model";
import { createPromiseApiAction } from "cortex/ui/src/redux-store/api-sagas/actions";
import { addErrorMessageAction } from "cortex/ui/src/redux-store/messages/actions";
import { CREATE_NETWORK } from "cortex/ui/src/redux-store/networks/actions";

interface NetworkCreateDrawerProps {
  onCancel: () => void;
  open: boolean;
  vistarNetworks?: ReadonlyArray<VistarNetwork>;
}

type Option = {
  accountId: string;
  accountName: string;
  groupBy?: string;
  label: string;
  value: number;
};

export default function NetworkCreateDrawer({
  onCancel,
  open,
  vistarNetworks,
}: NetworkCreateDrawerProps) {
  const dispatch = useAppDispatch();
  const [networkName, setNetworkName] = useState<string>("");
  const [vistarNetwork, setVistarNetwork] = useState<VistarNetwork | null>(
    null,
  );
  const [error, setError] = useState<string | null>(null);

  const handleCancel = useCallback(() => {
    setNetworkName("");
    setError(null);
    onCancel();
  }, [onCancel]);

  const validateName = (name: string): string | null => {
    if (!name.trim()) {
      return "Network name is required.";
    }
    if (name.startsWith("_")) {
      return "Network name cannot start with an underscore.";
    }
    return null;
  };

  const handleCreateNetwork = useCallback(async () => {
    const validationError = validateName(networkName);
    if (validationError) {
      setError(validationError);
      return;
    }

    try {
      await createPromiseApiAction(dispatch, CREATE_NETWORK, [
        networkName,
        vistarNetwork ? vistarNetwork : undefined,
      ]);
      handleCancel();
    } catch (_err) {
      dispatch(addErrorMessageAction("Failed to create network"));
    }
  }, [dispatch, handleCancel, networkName]);

  const vistarNetworkOptions: Option[] = useMemo(() => {
    if (!vistarNetworks?.length) return [];
    const raw: Option[] = vistarNetworks.map((n) => ({
      label: n.name ?? n.account_name,
      value: n.id,
      accountId: n.account_id,
      accountName: n.account_name,
    }));
    return raw.sort((a, b) => {
      const g = a.accountName.localeCompare(b.accountName, undefined, {
        sensitivity: "base",
      });
      if (g !== 0) return g;
      return a.label.localeCompare(b.label, undefined, { sensitivity: "base" });
    });
  }, [vistarNetworks]);

  const selectedOption: Option | undefined = useMemo(() => {
    if (!vistarNetwork) return undefined;
    const n = vistarNetworks?.find((x) => x.id === vistarNetwork.id);
    return n
      ? {
          label: n.name ?? n.account_name,
          value: n.id,
          accountId: n.account_id,
          accountName: n.account_name,
        }
      : undefined;
  }, [vistarNetwork, vistarNetworks]);

  return (
    <Drawer
      onCancel={handleCancel}
      onClose={handleCancel}
      onCommit={handleCreateNetwork}
      open={open}
      title="Add Network"
    >
      <Stack direction="column" spacing={2}>
        {vistarNetworks && vistarNetworkOptions.length > 0 && (
          <AutocompleteSelectInput
            disableClearable
            getOptionKey={(opt) => opt.value}
            getOptionLabel={(opt) => opt.label}
            groupBy={(opt) => opt.accountName}
            inputProps={{
              helperText:
                "Please select the related Vistar Trafficking network if applicable.",
              label: "Vistar Network",
            }}
            isOptionEqualToValue={(opt: Option, val: Option) =>
              String(opt.value) === String(val.value)
            }
            onChange={(_e, opt: Option | null) => {
              setVistarNetwork(
                opt
                  ? vistarNetworks?.find((n) => n.id === opt.value) || null
                  : null,
              );
              setNetworkName(
                opt
                  ? vistarNetworks?.find((n) => n.id === opt.value)?.name || ""
                  : "",
              );
            }}
            options={vistarNetworkOptions}
            size="small"
            value={selectedOption}
          />
        )}
        <TextInput
          autoFocus
          error={Boolean(error)}
          fullWidth
          helperText={
            error ??
            "Required and network names cannot start with an underscore."
          }
          label="Enter network name"
          onChange={(newValue: string | null) => setNetworkName(newValue ?? "")}
          required
          value={networkName}
        />
      </Stack>
    </Drawer>
  );
}
